import React, { Fragment, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation, matchPath } from "react-router-dom";
import { ChevronDown } from "react-feather";
import { MENUITEMS, usePendingOvertimeCount, PendingBadge } from "./Menu";
import { getLocalItem, ROLE, ROLE_PERMISSION } from "../../Constant/common";
import { SuperAdmin } from "../../Constant";
import { routes } from "../../Routes/Routes";

/* ── Flyout rendered via portal — escapes sidebar overflow:hidden ─────── */
const FlyoutPortal = ({ top, label, flyoutLinks }) => {
  const SIDEBAR_COLLAPSED_WIDTH = 68;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        left: SIDEBAR_COLLAPSED_WIDTH + 8,
        top,
        transform: "translateY(-50%)",
        zIndex: 99999,
        pointerEvents: "auto",
      }}
    >
      {/* Arrow */}
      <div
        style={{
          position: "absolute",
          left: -6,
          top: "50%",
          transform: "translateY(-50%)",
          width: 0,
          height: 0,
          borderTop: "6px solid transparent",
          borderBottom: "6px solid transparent",
          borderRight: "6px solid var(--dropdown-bg)",
          filter: "drop-shadow(-1px 0 0 var(--dropdown-border))",
        }}
      />
      <div
        style={{
          background: "var(--dropdown-bg)",
          border: "1px solid var(--dropdown-border)",
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
          backdropFilter: "blur(20px) saturate(180%)",
          minWidth: 190,
          overflow: "hidden",
          animation: "scaleIn 0.15s ease",
        }}
      >
        {/* Label header */}
        <div
          style={{
            padding: "9px 14px 7px",
            fontSize: "0.6875rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "var(--text-muted)",
            borderBottom: flyoutLinks ? "1px solid var(--glass-border)" : "none",
          }}
        >
          {label}
        </div>
        {flyoutLinks && <div style={{ padding: "4px" }}>{flyoutLinks}</div>}
      </div>
    </div>,
    document.body,
  );
};

/* ── Collapsed icon button with flyout ───────────────────────────────── */
const CollapsedFlyout = ({ label, icon: Icon, flyoutLinks, isActive, path, type }) => {
  const [visible, setVisible] = useState(false);
  const [top, setTop] = useState(0);
  const ref = useRef(null);

  const show = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTop(rect.top + rect.height / 2);
    }
    setVisible(true);
  };
  const hide = () => setVisible(false);

  const iconButton = (
    <div
      style={{
        width: 40,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 10,
        cursor: "pointer",
        background: isActive ? "rgba(99,102,241,0.12)" : "transparent",
        color: isActive ? "var(--brand-indigo)" : "var(--nav-text)",
        transition: "all 0.15s",
        position: "relative",
      }}
    >
      {isActive && (
        <span
          style={{
            position: "absolute",
            left: -8,
            top: "20%",
            bottom: "20%",
            width: 3,
            borderRadius: "0 3px 3px 0",
            background: "linear-gradient(180deg,#6366f1,#8b5cf6)",
          }}
        />
      )}
      {Icon && <Icon size={17} strokeWidth={2} />}
    </div>
  );

  // If it's a direct link (no children), wrap in Link component
  if (type === "link" && path) {
    return (
      <div ref={ref} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Link
          to={path}
          style={{ display: "flex", justifyContent: "center", textDecoration: "none" }}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          {iconButton}
        </Link>
        {visible && <FlyoutPortal top={top} label={label} flyoutLinks={null} />}
      </div>
    );
  }

  // Otherwise, show flyout with children
  return (
    <div
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={{ width: "100%", display: "flex", justifyContent: "center" }}
    >
      {iconButton}
      {visible && <FlyoutPortal top={top} label={label} flyoutLinks={flyoutLinks} />}
    </div>
  );
};

/* ── Main component ───────────────────────────────────────────────────── */
const SidebarMenuItems = ({ collapsed }) => {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const role = getLocalItem("role");
  const pendingOvertimeCount = usePendingOvertimeCount();

  const permissions = useMemo(() => {
    try {
      return JSON.parse(getLocalItem("permissions")) || [];
    } catch {
      return [];
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isModuleAllowed = (parentModule, module) => {
    if (parentModule === "Support") {
      return permissions.some(
        (p) =>
          p.parent_module_name?.trim()?.toLowerCase() === "support" ||
          p.module_name?.trim()?.toLowerCase() === "support",
      );
    }
    return permissions.some(
      (p) => p.parent_module_name === parentModule && p.module_name === module,
    );
  };

  const finalMenu = useMemo(() => {
    const menuCopy = MENUITEMS.map((item) => ({
      ...item,
      Items: item.Items.map((sub) => {
        const copy = { ...sub };
        if (sub.children) {
          copy.children = sub.children.map((c) => ({ ...c }));
        }
        return copy;
      }),
    }));

    let result = menuCopy
      .map((menuItem) => {
        if (menuItem.menutitle !== ROLE) {
          menuItem.Items = menuItem.Items.map((item) => {
            if (item.children) {
              item.children = item.children.filter((child) =>
                isModuleAllowed(menuItem.menutitle, child.title),
              );
            }
            return item;
          }).filter((item) =>
            item.children
              ? item.children.length > 0
              : isModuleAllowed(menuItem.menutitle, item.title),
          );
        } else {
          menuItem.Items = menuItem.Items.filter((item) =>
            isModuleAllowed(item.title, ROLE_PERMISSION),
          );
        }
        return menuItem;
      })
      .filter((menuItem) => menuItem.Items && menuItem.Items.length > 0);

    // Organization is SuperAdmin-only — strip it from the sidebar for all other roles
    if (role !== SuperAdmin) {
      result = result
        .map((group) => {
          if (group.menutitle === "Master") {
            return {
              ...group,
              Items: group.Items.map((item) => ({
                ...item,
                children: item.children?.filter((c) => c.title !== "Organization"),
              })).filter((item) => !item.children || item.children.length > 0),
            };
          }
          return group;
        })
        .filter((group) => group.Items && group.Items.length > 0);
    }

    // Payroll and Overtime are restricted for SuperAdmin — strip them from the sidebar
    if (role === SuperAdmin) {
      result = result
        .map((group) => {
          if (group.menutitle === "HR") {
            return {
              ...group,
              Items: group.Items.map((item) => ({
                ...item,
                children: item.children?.filter(
                  (c) => c.title !== "Payroll" && c.title !== "Overtime",
                ),
              })).filter((item) => !item.children || item.children.length > 0),
            };
          }
          return group;
        })
        .filter((group) => group.Items && group.Items.length > 0);
    }

    return result;
  }, [permissions, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  /* Highlight parent sidebar items if the user is on any of their subpages */
  const isExactActive = (path) => {
    if (!path) return false;
    const current = location.pathname.toLowerCase();
    const itemPath = path.toLowerCase();

    // 1. Direct exact match
    if (current === itemPath) return true;

    // 2. Dynamic match using parent_path defined on routes
    const activeRoute = routes.find((r) =>
      matchPath({ path: r.path, end: true }, location.pathname),
    );

    return activeRoute?.parent_path?.toLowerCase() === itemPath;
  };
  const isChildActive = (children) => children?.some((c) => isExactActive(c.path));

  return (
    <Fragment>
      <nav aria-label="Main navigation" style={{ padding: "6px 0", width: "100%" }}>
        {finalMenu.map((group, gi) => (
          <div key={gi}>
            {/* ── Section divider / label ── */}
            {!collapsed ? (
              <div
                style={{
                  padding: gi === 0 ? "8px 16px 4px" : "16px 16px 4px",
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  userSelect: "none",
                }}
              >
                {group.menutitle}
              </div>
            ) : (
              gi > 0 && (
                <div
                  style={{
                    margin: "6px 14px",
                    height: 1,
                    background: "var(--glass-border)",
                  }}
                />
              )
            )}

            {group.Items.map((menuItem, mi) => {
              const groupKey = `${gi}-${mi}`;
              const hasChildren = menuItem.children && menuItem.children.length > 0;
              const childActive = isChildActive(menuItem.children);
              const isOpen = openGroups[groupKey] ?? childActive;
              const Icon = menuItem.icon;
              /* Parent is active only if it's a direct link AND exactly matches */
              const selfActive = menuItem.type !== "sub" && isExactActive(menuItem.path);
              const parentHighlighted = selfActive || childActive;

              /* ── COLLAPSED MODE ── */
              if (collapsed) {
                const flyoutChildren = hasChildren
                  ? menuItem.children.map((child, ci) => {
                      const ChildIcon = child.icon;
                      const active = isExactActive(child.path);
                      return (
                        <Link
                          key={ci}
                          to={child.path}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 12px",
                            borderRadius: 8,
                            color: active ? "var(--brand-indigo)" : "var(--text-primary)",
                            fontWeight: active ? 600 : 400,
                            fontSize: "0.8125rem",
                            textDecoration: "none",
                            background: active ? "rgba(99,102,241,0.1)" : "transparent",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            if (!active) e.currentTarget.style.background = "var(--nav-hover)";
                          }}
                          onMouseLeave={(e) => {
                            if (!active) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {ChildIcon && (
                            <ChildIcon size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
                          )}
                          <span style={{ flex: 1 }}>{child.title}</span>
                          {child.badge === "pendingOvertime" && (
                            <PendingBadge count={pendingOvertimeCount} />
                          )}
                        </Link>
                      );
                    })
                  : null;

                return (
                  <div key={mi} style={{ display: "flex", justifyContent: "center" }}>
                    <CollapsedFlyout
                      label={menuItem.title}
                      icon={Icon}
                      flyoutLinks={flyoutChildren}
                      isActive={parentHighlighted}
                      path={menuItem.path}
                      type={menuItem.type}
                    />
                  </div>
                );
              }

              /* ── EXPANDED MODE ── */
              const rowStyle = {
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                margin: "1px 8px",
                width: "calc(100% - 16px)",
                borderRadius: 10,
                background: parentHighlighted ? "rgba(99,102,241,0.1)" : "transparent",
                color: parentHighlighted ? "var(--brand-indigo)" : "var(--nav-text)",
                fontSize: "0.8125rem",
                fontWeight: parentHighlighted ? 600 : 500,
                textDecoration: "none",
                transition: "all 0.15s",
                position: "relative",
                cursor: "pointer",
                border: "none",
                textAlign: "left",
              };

              return (
                <div key={mi}>
                  {menuItem.type === "sub" ? (
                    <button
                      onClick={() => toggleGroup(groupKey)}
                      aria-expanded={isOpen}
                      style={rowStyle}
                      onMouseEnter={(e) => {
                        if (!parentHighlighted)
                          e.currentTarget.style.background = "var(--nav-hover)";
                      }}
                      onMouseLeave={(e) => {
                        if (!parentHighlighted) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {parentHighlighted && (
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "18%",
                            bottom: "18%",
                            width: 3,
                            borderRadius: "0 3px 3px 0",
                            background: "linear-gradient(180deg,#6366f1,#8b5cf6)",
                          }}
                        />
                      )}
                      {Icon && <Icon size={16} strokeWidth={2} style={{ flexShrink: 0 }} />}
                      <span style={{ flex: 1 }}>{menuItem.title}</span>
                      <ChevronDown
                        size={13}
                        strokeWidth={2.5}
                        style={{
                          flexShrink: 0,
                          color: "var(--text-muted)",
                          transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                          transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
                        }}
                      />
                    </button>
                  ) : (
                    <Link
                      to={menuItem.path}
                      style={rowStyle}
                      onMouseEnter={(e) => {
                        if (!parentHighlighted)
                          e.currentTarget.style.background = "var(--nav-hover)";
                      }}
                      onMouseLeave={(e) => {
                        if (!parentHighlighted) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {parentHighlighted && (
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "18%",
                            bottom: "18%",
                            width: 3,
                            borderRadius: "0 3px 3px 0",
                            background: "linear-gradient(180deg,#6366f1,#8b5cf6)",
                          }}
                        />
                      )}
                      {Icon && <Icon size={16} strokeWidth={2} style={{ flexShrink: 0 }} />}
                      <span>{menuItem.title}</span>
                    </Link>
                  )}

                  {/* ── Submenu accordion ── */}
                  {hasChildren && (
                    <div
                      style={{
                        overflow: "hidden",
                        maxHeight: isOpen ? `${menuItem.children.length * 38}px` : "0px",
                        transition: "max-height 0.28s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    >
                      <div style={{ padding: "2px 8px 4px 36px" }}>
                        {menuItem.children.map((child, ci) => {
                          const ChildIcon = child.icon;
                          const active = isExactActive(child.path);
                          return (
                            <Link
                              key={ci}
                              to={child.path}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "7px 10px",
                                borderRadius: 8,
                                background: active ? "rgba(99,102,241,0.1)" : "transparent",
                                color: active ? "var(--brand-indigo)" : "var(--text-secondary)",
                                fontSize: "0.8rem",
                                fontWeight: active ? 600 : 400,
                                textDecoration: "none",
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                if (!active) {
                                  e.currentTarget.style.background = "var(--nav-hover)";
                                  e.currentTarget.style.color = "var(--brand-indigo)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!active) {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = "var(--text-secondary)";
                                }
                              }}
                            >
                              {ChildIcon ? (
                                <ChildIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                              ) : (
                                <span
                                  style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    background: active
                                      ? "var(--brand-indigo)"
                                      : "var(--text-muted)",
                                    transition: "background 0.15s",
                                  }}
                                />
                              )}
                              <span style={{ flex: 1 }}>{child.title}</span>
                              {child.badge === "pendingOvertime" && (
                                <PendingBadge count={pendingOvertimeCount} />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
    </Fragment>
  );
};

export default SidebarMenuItems;
